import { useEffect } from 'react';

import { setUserTokenInLS } from 'src/utils/auth-utils/TokenHandler';

/** @jsxRuntime classic */
/** @jsxImportSource @emotion/react */
// import { jsx } from '@emotion/react';
import { useAppStore } from 'src/hooks/useAppStore';
import {
  googleLoginAPI,
  googleRegisterAPI,
  twitterLoginAPI,
  twitterRegisterAPI
} from '../../services/apiRequests/Login/LoginService';
import SimpleStore from '../../store/SimpleStore';
import { setLocalStorage } from '../../utils/browserStorageHandler';
import { HTTP_HEADER_KEYS } from '../../utils/constants/HttpMethodsConstants';
import { USER_TOKEN } from '../../utils/constants/localStorageConstants';
import {
  LINK_TYPE,
  LOGIN_REGISTER_TEXT
} from '../../utils/constants/stringConstants';
import {
  loadAppleLoginScript,
  loadFbLoginScript,
  loadGoogleLoginScript
} from '../../utils/scripts/socialLoginScripts';
import * as style from './SocialMediaLoginCss';
import TwitterLoginComponent from './TwitterLogin';
import { appleLogin, fbLogin, SOCIAL_REGISTER_CONSENTS } from './Utils';

interface SocialMediaLoginProps {
  onClose?: () => void;
  className?: string;
  handleSocialLinking?: (linkingType: string, accessToken: string) => void;
  islinking?: boolean;
}

interface ITwitterAuthData {
  oauth_token: string;
  oauth_token_secret: string;
  user_id: string;
  screen_name: string;
}

const loadSocialMediaLoginScripts = () => {
  loadAppleLoginScript();
  loadFbLoginScript();
  loadGoogleLoginScript();
};

const SocialMediaLogin = ({
  onClose,
  handleSocialLinking,
  islinking,
  className
}: SocialMediaLoginProps) => {
  const state = useAppStore();
  const LOGIN_REGISTER_STRINGS = state.translations?.loginRegister;

  useEffect(() => {
    {
      !state.appConfig.isRequestedByBot ? loadSocialMediaLoginScripts() : null;
    }
    const interval = setInterval(() => {
      if (window && window.gapi && window.auth2) {
        handleGoogleLogin();
        clearInterval(interval);
      }
    }, 500);
  }, []);

  const handleAppleRegistration = () => {
    if (window?.AppleID?.auth) {
      onClose && appleLogin(onClose);
    }
  };

  const handleFbLogin = () => {
    onClose && fbLogin(onClose);
  };

  const handleGoogleLogin = () => {
    if (window?.gapi && window?.auth2) {
      const element = document.getElementById('gbtn');
      window.auth2.attachClickHandler(
        element,
        {},
        () => {
          const googleToken = window.gapi.auth2
            .getAuthInstance()
            .currentUser.get()
            .getAuthResponse(true).access_token;
          const reqObj = {
            access_token: googleToken
          };

          if (islinking) {
            handleSocialLinking &&
              handleSocialLinking(LINK_TYPE.GOOGLE_LINK, googleToken);
            return;
          }
          //GOOGLE Login API call
          googleLoginAPI(reqObj)
            .then((loginData) => {
              if (loginData.access_token) {
                setLocalStorage(USER_TOKEN, loginData.access_token);
                SimpleStore.set(
                  HTTP_HEADER_KEYS.AUTHORIZATION,
                  loginData.access_token
                );
                if (onClose) {
                  onClose();
                }
              }
              if (loginData.code === 2) {
                //user not registered with Zee5
                const reqObjRegister = {
                  token: googleToken,
                  ...SOCIAL_REGISTER_CONSENTS
                };
                googleRegisterAPI(reqObjRegister).then((registerData) => {
                  if (registerData.access_token) {
                    setLocalStorage(USER_TOKEN, registerData.access_token);
                    SimpleStore.set(
                      HTTP_HEADER_KEYS.AUTHORIZATION,
                      registerData.access_token
                    );
                    if (onClose) {
                      onClose();
                    }
                  }
                });
              }
            })
            .catch((err) => {
              console.error('Google login API error', err);

              if (err?.responseData?.code === 2) {
                //user not registered with Zee5
                const reqObjRegister = {
                  token: googleToken,
                  ...SOCIAL_REGISTER_CONSENTS
                };
                googleRegisterAPI(reqObjRegister).then((registerData) => {
                  if (registerData.access_token) {
                    setLocalStorage(USER_TOKEN, registerData.access_token);
                    SimpleStore.set(
                      HTTP_HEADER_KEYS.AUTHORIZATION,
                      registerData.access_token
                    );
                    if (onClose) {
                      onClose();
                    }
                  }
                });
              }
            });
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  };

  const handleTwitterLogin = (
    err: string | null | undefined,
    data: ITwitterAuthData
  ) => {
    if (data?.oauth_token && data?.oauth_token_secret) {
      const reqObjTwitter = {
        access_token: `${data.oauth_token}|${data.oauth_token_secret}`
      };
      // handle linking block
      if (islinking) {
        handleSocialLinking &&
          handleSocialLinking(
            LINK_TYPE.TWITTER_LINK,
            `${data.oauth_token}|${data.oauth_token_secret}`
          );
        return;
      }
      twitterLoginAPI(reqObjTwitter)
        .then((loginData) => {
          if (loginData.access_token) {
            setUserTokenInLS(loginData.access_token);
            if (onClose) {
              onClose();
            }
          }
          if (loginData.code === 2) {
            //user not registered with Zee5
            const reqObjRegister = {
              token: reqObjTwitter.access_token,
              ...SOCIAL_REGISTER_CONSENTS
            };
            twitterRegisterAPI(reqObjRegister).then((registerData) => {
              if (registerData.access_token) {
                setLocalStorage(USER_TOKEN, registerData.access_token);
                SimpleStore.set(
                  HTTP_HEADER_KEYS.AUTHORIZATION,
                  registerData.access_token
                );
                if (onClose) {
                  onClose();
                }
              }
            });
          }
        })
        .catch((err) => console.error('Google login API error', err));
    }
  };

  return (
    <div
      css={style.socialDiv}
      data-test-id="socialDiv"
      className={className || 'SocialLoginWrap'}>
      <div
        css={style.loginhrLine}
        data-test-id="loginhrLine"
        className={className && 'socialOr'}>
        <div>
          <span css={style.loginhrLineSpan}>{LOGIN_REGISTER_STRINGS?.OR}</span>
        </div>
      </div>

      <div data-test-id="socialIconsFooter" css={style.socialIconsFooter}>
        <div
          data-testid="handle-apple-registration"
          css={[style.socialIcons, style.LogoIcon]}
          className="loginSocialIcon socialAppleLogin"
          onClick={handleAppleRegistration}>
          <img
            src="/webapp-assets/images/apple-social-icon-circle.svg"
            alt={LOGIN_REGISTER_TEXT.APPLE}
          />
        </div>
        <div
          data-test-id="socialIcons"
          css={[style.socialIcons, style.LogoIcon]}
          className="loginSocialIcon socialGoogleLogin"
          id="gbtn"
          data-testid="handle-google-login"
          onClick={handleGoogleLogin}>
          <img
            src="/webapp-assets/images/google-social-icon-circle.svg"
            alt={LOGIN_REGISTER_TEXT.GOOGLE}
          />
        </div>
        <div
          css={style.socialIcons}
          data-test-id="socialIcons"
          className="loginSocialIcon socialFacebookLogin"
          onClick={handleFbLogin}>
          <img
            src="/webapp-assets/images/fb-social-icon-circle.svg"
            alt={LOGIN_REGISTER_TEXT.FACEBOOK}
          />
        </div>
        <TwitterLoginComponent
          consumerKey={
            process.env.NEXT_PUBLIC_TWITTER_CONSUMER_KEY
              ? process.env.NEXT_PUBLIC_TWITTER_CONSUMER_KEY
              : ''
          }
          consumerSecret={
            process.env.NEXT_PUBLIC_TWITTER_CONSUMER_SECRET
              ? process.env.NEXT_PUBLIC_TWITTER_CONSUMER_SECRET
              : ''
          }
          authCallback={handleTwitterLogin}
        />
      </div>
    </div>
  );
};

export default SocialMediaLogin;
